"use client";

import { useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { ShoppingCart, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
} from "@/components/ui/empty";
import { useCartStore, selectEntries, selectValidatedItems, selectOutOfStockValidatedItems, selectIsRefreshing, selectTotalItems } from "@/lib/cart/cart-store";
import { useRefreshCart } from "@/hooks/use-refresh-cart";
import { fetchAndSyncCouponDetails } from "@/hooks/use-fetch-coupons";
import { useCouponStore } from "@/lib/coupons/coupon-store";
import { useOrderTotals } from "@/hooks/use-order-totals";
import CartItemRow from "@/components/cart/cart-item-row";
import FreeDeliveryProgress from "@/components/cart/free-delivery-progress";
import OrderSummaryLines from "@/components/checkout/order-summary-lines";
import { Separator } from "../ui/separator";
import { useAuth } from "@/context/auth-context";
import CaliiPlusCheckbox, { CaliiPlusMemberBanner } from "@/components/cart/calii-plus-checkbox";
import { trackAction } from "@/lib/track-action";

type CartSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export default function CartDetailsSheet({ open, onOpenChange }: CartSheetProps) {
  const { user } = useAuth();
  const entries = useCartStore(selectEntries);
  const appliedCodes = useCouponStore((s) => s.appliedCodes);
  const validatedItems = useCartStore(selectValidatedItems);
  const outOfStockItems = useCartStore(selectOutOfStockValidatedItems);
  const isRefreshing = useCartStore(selectIsRefreshing);
  const lastRefreshedAt = useCartStore((s) => s.lastRefreshedAt);
  const totalItems = useCartStore(selectTotalItems);
  const refreshCart = useRefreshCart();
  const orderTotals = useOrderTotals();
  const router = useRouter();
  const hasAnyItems = entries.length > 0;

  useEffect(() => {
    if (open) {
      trackAction("CART-OPENED");
      refreshCart();
      fetchAndSyncCouponDetails(appliedCodes);
    }
  }, [open, refreshCart, appliedCodes]);

  const handleContinue = () => {
    trackAction("CHECKOUT-STARTED");
    onOpenChange(false);
    router.push("/checkout");
  };

  const noValidatedData = validatedItems.length === 0 && outOfStockItems.length === 0;
  const showSkeleton = noValidatedData && (isRefreshing || !lastRefreshedAt);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col">
        <SheetHeader>
          <SheetTitle>Carrito de compras</SheetTitle>
          {totalItems > 0 && (
            <p className="text-sm text-muted-foreground">
              {totalItems} {totalItems === 1 ? "producto" : "productos"}
            </p>
          )}
        </SheetHeader>

        {!hasAnyItems ? (
          <div className="flex-1 flex items-center justify-center">
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <ShoppingCart className="h-6 w-6" />
                </EmptyMedia>
                <EmptyTitle>Tu carrito está vacío</EmptyTitle>
                <EmptyDescription>
                  Agrega productos para comenzar tu pedido
                </EmptyDescription>
              </EmptyHeader>
              <Button onClick={() => onOpenChange(false)} asChild className="cursor-pointer bg-teal-400 hover:bg-teal-500">
                <Link href="/">Seguir comprando</Link>
              </Button>
            </Empty>
          </div>
        ) : showSkeleton ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6 -mt-4">
              <Separator className="" />
              <FreeDeliveryProgress className="px-16 py-4" />
              <Separator className="mb-2" />
              {/* Available items */}
              <div className="divide-y">
                {validatedItems.map((item) => (
                  <CartItemRow key={item.productId} item={item} />
                ))}
              </div>

              {/* Out-of-stock items */}
              {outOfStockItems.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 mx-5">
                    Temporalmente agotado{outOfStockItems.length > 1 ? "s" : ""}
                  </p>
                  <div className="divide-y opacity-50">
                    {outOfStockItems.map((item) => (
                      <div key={item.productId} className="flex gap-3 py-3 px-4">
                        <div className="relative w-14 h-14 rounded-lg overflow-hidden flex-shrink-0">
                          <Image
                            src={item.imageUrl}
                            alt={item.name}
                            fill
                            className="object-cover"
                            sizes="56px"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-medium text-gray-800 line-clamp-1">
                            {item.name}
                          </h3>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Agotado
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="px-4 pt-4 pb-2 border-t mt-2">
                <OrderSummaryLines totals={orderTotals} />
              </div>
            </div>

            {/* Combined footer — -mt-4 cancels SheetContent's gap-4 so it sits flush below the scroll area */}
            <div className="-mt-4 border-t">
              {user?.is_calii_plus_member ? <CaliiPlusMemberBanner className="mb-4" /> : <CaliiPlusCheckbox className="py-3 px-4" />}
              <div className="px-4 pb-4">
                <Button
                  className="w-full bg-teal-400 hover:bg-teal-500 cursor-pointer"
                  size="lg"
                  onClick={handleContinue}
                  disabled={validatedItems.length === 0}
                >
                  Continuar
                </Button>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
